import { RATING_NEUTRAL } from './constants.js';

/**
 * Man of the match suggestion.
 *
 * Best performance rating wins; ties broken by goals + assists, then by goals
 *
 * Since rating moved off the Log screen there is no performance to compare on a
 * new game, so in practice this now ranks on goals and assists and returns null
 * when two players are level. That's the honest answer: at submit time nobody
 * has said anything about how people played yet. It is only ever a suggestion —
 * the group's votes on History override it.
 * alone. If it still can't separate people it returns null and the admin picks.
 *
 * The old version broke every remaining tie by array order, which quietly meant
 * "whoever happens to be first in Team A" was named man of the match in any game
 * where nobody stood out — a silent, systematic bias toward one team.
 */
/**
 * Decides who actually won man of the match for a finished game.
 *
 * Votes win when there are any — the admin's pick is only the fallback for a
 * game nobody voted on. Previously the admin's choice was written straight to
 * the game and that was the end of it, so nobody else's opinion counted at all.
 *
 * A tie is resolved by the admin's pick if it's one of the tied players, and
 * otherwise left genuinely undecided rather than broken arbitrarily.
 */
export function resolveMotm({ votes = [], adminPickId = null }) {
  const counts = {};
  votes.forEach((v) => {
    counts[v.recipient_member_id] = (counts[v.recipient_member_id] || 0) + 1;
  });

  const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  if (!entries.length) {
    return { memberId: adminPickId, votes: 0, counts, source: adminPickId ? 'admin' : 'none' };
  }

  const top = entries[0][1];
  const tied = entries.filter(([, c]) => c === top);

  if (tied.length === 1) return { memberId: tied[0][0], votes: top, counts, source: 'vote' };

  const adminBreaksIt = tied.find(([id]) => id === adminPickId);
  if (adminBreaksIt) return { memberId: adminPickId, votes: top, counts, source: 'vote-tie-admin' };

  return { memberId: null, votes: top, counts, tied: tied.map(([id]) => id), source: 'tie' };
}

export function suggestManOfTheMatch(players, results) {
  if (!players?.length) return null;

  const scored = players.map((p) => {
    const r = results?.[p.id] || {};
    return {
      player: p,
      perf: r.performance ?? RATING_NEUTRAL,
      output: (r.goals || 0) + (r.assists || 0),
      goals: r.goals || 0,
    };
  });

  const rank = (a, b) => b.perf - a.perf || b.output - a.output || b.goals - a.goals;
  const sorted = [...scored].sort(rank);
  const best = sorted[0];
  const tied = sorted.filter((s) => rank(s, best) === 0);

  return tied.length === 1 ? best.player : null;
}
